/**
 * A signed-out buyer's favourites, on this device.
 *
 * IDS ONLY, NEVER A SNAPSHOT. Storing the name, image or price here is how a
 * saved card comes back showing last week's figure; `cards-by-ids/` resolves
 * these to live cards when the collection page needs them.
 *
 * VERSIONED so a shape change can be discarded rather than mis-parsed. Anything
 * this module cannot read confidently -- bad JSON, an unknown version, a
 * non-array -- reads as empty rather than throwing, because a corrupt
 * localStorage value must never take down a browse page.
 */
export const GUEST_FAVOURITES_KEY = 'favourites_guest';

/* 50 keeps the payload near 2KB and the sign-in merge to at most 50 idempotent
   POSTs. A guest past 50 is well beyond the point where signing in is the
   obvious move. */
export const GUEST_FAVOURITES_CAP = 50;

const VERSION = 1;

export function readGuestFavourites() {
  if (typeof localStorage === 'undefined') return [];
  try {
    const raw = localStorage.getItem(GUEST_FAVOURITES_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!parsed || parsed.v !== VERSION || !Array.isArray(parsed.ids)) return [];
    return parsed.ids.filter((id) => typeof id === 'string' && id.length > 0);
  } catch (error) {
    return [];
  }
}

export function writeGuestFavourites(ids) {
  if (typeof localStorage === 'undefined') return;
  /* Oldest first, so trimming to the cap drops the front. */
  const trimmed = ids.slice(-GUEST_FAVOURITES_CAP);
  try {
    localStorage.setItem(GUEST_FAVOURITES_KEY, JSON.stringify({ v: VERSION, ids: trimmed }));
  } catch (error) {
    /* Quota or private-mode refusal. Losing a favourite is survivable; throwing
       out of a heart tap is not. */
  }
}

export function toggleGuestFavourite(cardId) {
  const current = readGuestFavourites();
  const next = current.includes(cardId)
    ? current.filter((id) => id !== cardId)
    : [...current, cardId];
  writeGuestFavourites(next);
  return readGuestFavourites();
}

/**
 * Drops specific ids and leaves the rest.
 *
 * The sign-in merge needs this rather than `clearGuestFavourites`: it saves the
 * ids it can and must leave behind the ones it could not, so a single failure
 * cannot take the whole list with it. `/collection` uses it too, to prune ids
 * the catalog no longer resolves.
 */
export function removeGuestFavourites(idsToRemove) {
  const drop = new Set(idsToRemove || []);
  if (drop.size === 0) return readGuestFavourites();
  const remaining = readGuestFavourites().filter((id) => !drop.has(id));
  if (remaining.length === 0) {
    clearGuestFavourites();
    return [];
  }
  writeGuestFavourites(remaining);
  return readGuestFavourites();
}

export function clearGuestFavourites() {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.removeItem(GUEST_FAVOURITES_KEY);
  } catch (error) {
    /* Same reasoning as the write. */
  }
}
